import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { theme } from 'styled-tools';

import ExpandableContent from '@ui/expandableContent.tsx';
import { useFocusTrap } from '@ui/modals/FocusTrap.tsx';
import { fadeIn } from '@utils/animations.ts';

import type { PreviewEndpoint, PreviewLeg, ShowRoutePreviewPayload } from '../../../../../src/types/route-preview.ts';
import { getRoutePreviewSource } from '../helpers/routePreviewState.ts';
import { RouteSteps, RouteSummary, buildRouteStepsData, buildRouteSummaryData } from '../RouteSummary/index.ts';

type Bus = { send: (event: string, data?: unknown) => unknown };

/** Subset of the widget state the preview reads. All arrays are indexed per leg, except `endpoints`. */
type PreviewWidgetState = Parameters<typeof getRoutePreviewSource>[0] & {
  segments: unknown[];
  endpointIds?: ShowRoutePreviewPayload['endpointIds'];
  overviewHeaderRef?: { current: HTMLElement | null };
};

type FullRoutePreviewProps = {
  state: PreviewWidgetState;
  bus: Bus;
  T: (key: string, options?: Record<string, unknown>) => string;
  locale: string;
  isDesktop: boolean;
  onStart: () => unknown;
  focusOnMount?: boolean;
  /** Measured by the mobile route overview to keep the map clear of the sheet. */
  containerRef?: React.RefObject<HTMLDivElement>;
};

const MINUTE_MS = 60_000;

/** The arrival time is derived from the current time, so refresh it while the preview stays open. */
const useMinuteClock = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), MINUTE_MS);
    return () => clearInterval(timer);
  }, []);
  return now;
};

const useRoutePreviewContent = (state: PreviewWidgetState, T: FullRoutePreviewProps['T'], locale: string) => {
  const now = useMinuteClock();
  const { steps, times, distances, endpoints, searchInputs, preferredUnits } = state;
  return useMemo(() => {
    const source = getRoutePreviewSource({ steps, times, distances, endpoints, searchInputs, preferredUnits });
    return {
      summary: buildRouteSummaryData(source, { T, locale, now }),
      directions: buildRouteStepsData(source, { T }),
    };
  }, [steps, times, distances, endpoints, searchInputs, preferredUnits, T, locale, now]);
};

/** Shows the whole selected route on the map while the preview is mounted and clears it on exit. */
const useMapRoutePreview = (bus: Bus, state: PreviewWidgetState) => {
  const { steps, segments, endpoints, endpointIds } = state;

  useEffect(() => {
    const payload: ShowRoutePreviewPayload = {
      routes: steps.map((legSteps, index) => ({
        segments: segments[index],
        steps: legSteps,
      })) as PreviewLeg[],
      endpoints: endpoints as PreviewEndpoint[],
      endpointIds,
    };
    bus.send('map/showRoutePreview', payload);
  }, [bus, steps, segments, endpoints, endpointIds]);

  useEffect(() => () => void bus.send('map/clearRoutePreview'), [bus]);
};

const DesktopPanel = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 16px;

  /* Keep the app's keyboard focus outline inside the clipped sidebar. */
  &:focus-visible {
    outline-offset: -3px;
  }
`;

const Divider = styled.div<{ $inset?: boolean }>`
  flex: none;
  margin-block: 20px 10px;
  margin-inline: ${props => (props.$inset ? 16 : 0)}px;
  border-block-start: ${theme('borderWidths.sm')} solid ${theme('colors.secondaryButtonHover')};
`;

const DesktopSummary = styled.div`
  flex: none;
`;

const DesktopDirectionsCard = styled.div`
  display: flex;
  flex-direction: column;
  flex: none;
  min-height: 0;
  overflow: hidden;
`;

const MobileSheet = styled.div<{ $isOpen?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  min-height: 0;
  background-color: ${theme('colors.background')};
  overflow-y: ${props => (props.$isOpen ? 'auto' : 'none')};
`;

const SummaryArea = styled.div<{ $isOpen?: boolean }>`
  flex: none;
  padding: 14px 16px ${props => (props.$isOpen ? 0 : 20)}px;
`;

const DirectionsCard = styled.div`
  display: flex;
  flex-direction: column;
  flex: none;
`;

// The pill toggler above this header is a fixed 25px (ExpandableContent's PillToggler); Figma
// specs 58px for the whole collapsed sheet, so this header fills the remaining 33px.
const COLLAPSED_HEADER_HEIGHT = 33;
// 26px line-height + 13px gap exceeds the 33px box, so the text sits at the bottom with that gap
// as padding; the overflow goes above (into the row's own leading), matching the same line-height
// vs. box-size trick RouteSteps' Title already relies on.
const COLLAPSED_HEADER_BOTTOM_GAP = 13;

const CollapsedHeader = styled.div`
  flex: none;
  box-sizing: border-box;
  display: flex;
  align-items: flex-end;
  height: ${COLLAPSED_HEADER_HEIGHT}px;
  padding-inline: 16px;
  padding-block-end: ${COLLAPSED_HEADER_BOTTOM_GAP}px;
  font-size: 20px;
  font-weight: ${theme('fontWeights.normal')};
  line-height: 26px;
  color: ${theme('colors.primaryText')};
  ${fadeIn()};
`;

// Swapping between the collapsed header and the summary/directions content is otherwise an
// instant DOM replacement mid-drag; fading the incoming content in quickly softens that cut.
const SwapFade = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  ${fadeIn()};
`;

type MobileContentProps = Pick<FullRoutePreviewProps, 'T' | 'onStart' | 'containerRef'> & {
  content: ReturnType<typeof useRoutePreviewContent>;
  panelId: string;
  collapsedDescriptionId: string;
  // Injected by ExpandableContent.
  isOpen?: boolean;
  isExpanding?: boolean;
  isCollapsed?: boolean;
  isCollapsing?: boolean;
  notifyAboutChange?: () => void;
};

const MobilePreviewModal = ({ panelId }: { panelId: string }) => {
  useLayoutEffect(() => {
    const panel = document.getElementById(panelId);
    const root = panel?.closest('.atrius-map') ?? panel?.parentElement;
    if (!panel || !root) {
      return;
    }
    const toggle = panel.querySelector<HTMLButtonElement>('[data-testid="PillToggle"]');
    const previousInert = new Map<Element, string | null>();
    const makeBackgroundInert = () => {
      let current: Element = panel;
      while (current !== root && current.parentElement) {
        for (const sibling of current.parentElement.children) {
          if (sibling !== current) {
            if (!previousInert.has(sibling)) {
              previousInert.set(sibling, sibling.getAttribute('inert'));
            }
            sibling.setAttribute('inert', '');
          }
        }
        current = current.parentElement;
      }
    };
    makeBackgroundInert();
    const observer = new MutationObserver(makeBackgroundInert);
    observer.observe(root, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      for (const [element, previous] of previousInert) {
        if (previous === null) {
          element.removeAttribute('inert');
        } else {
          element.setAttribute('inert', previous);
        }
      }
      requestAnimationFrame(() => {
        // Start/close unmount the panel and own their next focus target.
        if (panel.isConnected) {
          toggle?.focus({ preventScroll: true });
        }
      });
    };
  }, [panelId]);
  useFocusTrap(true, panelId);
  return null;
};

const MobileContent = ({
  T,
  content,
  onStart,
  containerRef,
  isOpen,
  isExpanding,
  isCollapsed,
  isCollapsing,
  notifyAboutChange,
  panelId,
  collapsedDescriptionId,
}: MobileContentProps) => {
  const [isModal, setIsModal] = useState(false);
  const showDirections = isOpen || isExpanding;
  const showCollapsedHeader = isCollapsed || isCollapsing;
  const position = showDirections ? 'expanded' : isCollapsed ? 'collapsed' : 'default';

  useLayoutEffect(() => {
    const panel = document.getElementById(panelId);
    const updateCoverage = () => setIsModal(Boolean(isOpen && panel?.getAttribute('role') === 'dialog'));
    updateCoverage();
    if (!panel || !isOpen) {
      return;
    }
    const observer = new MutationObserver(updateCoverage);
    observer.observe(panel, { attributes: true, attributeFilter: ['role'] });
    return () => observer.disconnect();
  }, [isOpen, panelId]);

  useEffect(() => {
    notifyAboutChange?.();
  }, [isOpen, notifyAboutChange]);

  // Collapsing unmounts RouteSummary (and its Start button); if focus was inside it, the browser
  // drops focus to <body>. Restore it to the pill, matching how MobilePreviewModal already
  // restores focus to the pill when the expanded sheet closes.
  const wasCollapsedHeaderShown = useRef(showCollapsedHeader);
  useEffect(() => {
    if (!wasCollapsedHeaderShown.current && showCollapsedHeader && document.activeElement === document.body) {
      const panel = document.getElementById(panelId);
      panel?.querySelector<HTMLButtonElement>('[data-testid="PillToggle"]')?.focus({ preventScroll: true });
    }
    wasCollapsedHeaderShown.current = showCollapsedHeader;
  }, [showCollapsedHeader, panelId]);

  return (
    <MobileSheet
      ref={containerRef}
      data-testid="FullRoutePreviewMobile"
      data-route-preview-position={position}
      $isOpen={isOpen}
    >
      {isModal && <MobilePreviewModal panelId={panelId} />}
      {showCollapsedHeader ? (
        <CollapsedHeader id={collapsedDescriptionId} data-testid="FullRoutePreviewCollapsedHeader">
          {T('getDirectionsFromTo:_duration_ • _distance_', {
            duration: content.directions.duration,
            distance: content.directions.distance,
          })}
        </CollapsedHeader>
      ) : (
        <SwapFade>
          <SummaryArea $isOpen={showDirections}>
            <RouteSummary T={T} {...content.summary} onStart={() => onStart()} />
          </SummaryArea>
          {showDirections && (
            <>
              <Divider $inset aria-hidden="true" data-testid="FullRoutePreviewDivider" />
              <DirectionsCard data-testid="FullRoutePreviewDirections">
                <RouteSteps T={T} {...content.directions} contentInset={16} />
              </DirectionsCard>
            </>
          )}
        </SwapFade>
      )}
    </MobileSheet>
  );
};

/** Production host for the route preview: the summary card and complete directions replace the legacy list. */
const FullRoutePreview = ({
  state,
  bus,
  T,
  locale,
  isDesktop,
  onStart,
  containerRef,
  focusOnMount = false,
}: FullRoutePreviewProps) => {
  const panelId = useId();
  const desktopPanelRef = useRef<HTMLDivElement>(null);
  const collapsedDescriptionId = `${panelId}-collapsed-summary`;
  const content = useRoutePreviewContent(state, T, locale);
  useMapRoutePreview(bus, state);

  useLayoutEffect(() => {
    if (focusOnMount) {
      const target = isDesktop
        ? desktopPanelRef.current
        : document.getElementById(panelId)?.querySelector<HTMLButtonElement>('[data-testid="PillToggle"]');
      target?.focus({ preventScroll: true });
    }
  }, [focusOnMount, isDesktop, panelId]);

  if (isDesktop) {
    return (
      <DesktopPanel
        ref={desktopPanelRef}
        data-testid="FullRoutePreviewDesktop"
        role="region"
        aria-label={T('getDirectionsFromTo:Route to _location_', { location: content.directions.destination })}
        tabIndex={0}
      >
        <DesktopSummary>
          <RouteSummary T={T} {...content.summary} onStart={() => onStart()} />
        </DesktopSummary>
        <Divider aria-hidden="true" data-testid="FullRoutePreviewDivider" />
        <DesktopDirectionsCard data-testid="FullRoutePreviewDesktopSteps">
          <RouteSteps T={T} {...content.directions} stickyHeader={false} />
        </DesktopDirectionsCard>
      </DesktopPanel>
    );
  }

  return (
    <ExpandableContent
      id={panelId}
      className="DirectionsResultSheet"
      ariaLabel={T('getDirectionsFromTo:Route directions')}
      maxHeight="100vh"
      bus={bus as never}
      isDraggable={true}
      T={T}
      isMapDisabled={true}
      hasInnerScrollingElement={true}
      handle="pill"
      expandToMaxHeight
      threePosition
      collapsedDescriptionId={collapsedDescriptionId}
    >
      <MobileContent
        T={T}
        content={content}
        onStart={onStart}
        containerRef={containerRef}
        panelId={panelId}
        collapsedDescriptionId={collapsedDescriptionId}
      />
    </ExpandableContent>
  );
};

export default FullRoutePreview;
